import MuiTab, { TabProps as MuiTabProps } from '@mui/material/Tab';
import MuiTabs from '@mui/material/Tabs';
import * as React from 'react';

import { FlexBox, FlexBoxProps } from '../box';
import { Button, IconButton } from '../button';
import { Chip } from '../chip';
import { Divider } from '../divider';
import { IconProps } from '../icon';
import { styled } from '../styled';
import { ColorProp, usePaletteColor } from '../theme';
import { Typography } from '../typography';
import { withVersion } from '../utils';

type TabItemHeader = {
    title: string;
    info?: string;
    actions?: {
        title: string;
        onClick?(): void | Promise<void>;
    }[];
};
type TabItemBase<P = object> = {
    label: string | number;
    content: React.ComponentType<P>;
    header?: TabItemHeader;
    disabled?: boolean;
    'data-role'?: string;
    keepMounted?: boolean;
};

type TabWithIcon = {
    icon: React.ComponentType<Pick<IconProps, 'color' | 'filled'>>;
    iconPosition?: 'left' | 'right';
    iconWrapper?: {
        width?: number | string;
        justifyContent?: FlexBoxProps['justifyContent'];
    };
    /**
     * Optional chip label displayed between the text and the icon.
     * Icon and chip can coexist on the same tab.
     */
    chipLabel?: string | number;
};

type TabWithChip = {
    chipLabel: string | number;
    icon?: never;
};

type PlainTab = {
    icon?: never;
    iconWrapper?: never;
    chipLabel?: never;
};

export type TabItemVariant = TabWithIcon | TabWithChip | PlainTab;

export type TabItem<P = object> = TabItemBase<P> & TabItemVariant;

export interface TabsStickyConfig {
    top?: number | string;
    backgroundColor?: ColorProp;
    contentPadding?: number;
}

export interface TabsProps {
    items: TabItem[];
    contentHeader?: React.ReactNode;
    /**
     * @default 'horizontal'
     */
    orientation?: 'horizontal' | 'vertical';
    /**
     * @default 2
     */
    contentGap?: number;
    selectedTabIndex?: number;
    onTabChange?: (activeTabIndex: number) => void;
    /**
     * @default false
     */
    sticky?: boolean | TabsStickyConfig;
    labelTextAlign?: FlexBoxProps['textAlign'];
}

interface TabLabelProps {
    item: TabItem;
    selected: boolean;
    labelTextAlign?: FlexBoxProps['textAlign'];
    isOrientationHorizontal?: boolean;
}

const TabLabel = ({
    item,
    selected,
    labelTextAlign,
    isOrientationHorizontal = true
}: TabLabelProps) => {
    const hasChipLabel =
        item.chipLabel !== null && item.chipLabel !== undefined;
    const chipEl = hasChipLabel ? (
        <Chip color="default" label={item.chipLabel} />
    ) : null;

    if (item.icon) {
        const iconEl = <item.icon color="inherit" filled={selected} />;
        const isRightIcon = item.iconPosition === 'right';
        const labelEl = chipEl ? (
            <FlexBox stretch>{item.label}</FlexBox>
        ) : (
            item.label
        );

        if (!isOrientationHorizontal) {
            return (
                <FlexBox
                    gap={1}
                    alignItems="center"
                    width="100%"
                    textAlign={labelTextAlign}
                >
                    {!isRightIcon && iconEl}
                    {labelEl}
                    {isRightIcon && iconEl}
                    {chipEl}
                </FlexBox>
            );
        }

        // Horizontal layout: left → [icon][text][chip], right → [text][icon][chip]
        return (
            <FlexBox
                gap={1}
                alignItems="center"
                textAlign={labelTextAlign}
                width={item.iconWrapper?.width}
                justifyContent={item.iconWrapper?.justifyContent}
            >
                {!isRightIcon && iconEl}
                {item.label}
                {isRightIcon && iconEl}
                {chipEl}
            </FlexBox>
        );
    }

    if (hasChipLabel) {
        if (!isOrientationHorizontal) {
            return (
                <FlexBox gap={1} alignItems="center" width="100%">
                    <FlexBox stretch>{item.label}</FlexBox>
                    {chipEl}
                </FlexBox>
            );
        }

        return (
            <FlexBox gap={1} alignItems="center">
                <FlexBox>{item.label}</FlexBox>
                {chipEl}
            </FlexBox>
        );
    }

    if (!isOrientationHorizontal) {
        return <FlexBox width="100%">{item.label}</FlexBox>;
    }

    return <>{item.label}</>;
};

const StyledFlexBox = styled(FlexBox)<{
    $isSticky: boolean;
    $stickyTop: number | string;
    $stickyBackgroundColor: string;
    $contentPadding?: number;
    $isOrientationHorizontal: boolean;
}>(
    ({
        $isSticky,
        $stickyTop,
        $stickyBackgroundColor,
        theme,
        $contentPadding,
        $isOrientationHorizontal
    }) => ({
        ...($isSticky && {
            height: 'fit-content',
            position: 'sticky',
            [$isOrientationHorizontal ? 'paddingBottom' : 'paddingTop']:
                theme.spacing($contentPadding ?? 1),
            top: $stickyTop,
            backgroundColor: $stickyBackgroundColor,
            zIndex: 1
        })
    })
);

const StyledMuiTabs = styled(MuiTabs)<{
    $isOrientationHorizontal: boolean;
    $indicatorColor?: string;
}>(({ theme, $isOrientationHorizontal, $indicatorColor }) => {
    const orientationStyles = $isOrientationHorizontal
        ? $indicatorColor
            ? {
                  '.MuiTabs-indicator': {
                      height: '4px',
                      borderRadius: '2px 2px 0 0',
                      backgroundColor: $indicatorColor
                  }
              }
            : {}
        : {
              minWidth: 'fit-content'
          };

    return {
        minHeight: 0,
        '.MuiTabs-scrollButtons': {
            color: theme.palette.action.active
        },
        ...orientationStyles
    };
});

const StyledMuiTab = styled(MuiTab)<{
    $color?: string;
    $selectedColor?: string;
    $activeBackgroundColor?: string;
    $labelTextAlign?: string;
    $isOrientationHorizontal?: boolean;
}>(
    ({
        $color,
        $selectedColor,
        $activeBackgroundColor,
        $labelTextAlign,
        $isOrientationHorizontal,
        theme
    }) => ({
        ...($labelTextAlign === 'left' && { alignItems: 'flex-start' }),
        ...($labelTextAlign === 'right' && { alignItems: 'flex-end' }),
        textTransform: 'none',
        borderRadius: 4,
        paddingTop: theme.spacing(1.125),
        paddingBottom: theme.spacing(1.125),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        minHeight: 0,
        lineHeight: theme.spacing(3),
        position: 'relative',
        color: $color,
        '&[aria-selected="true"]': {
            color: $selectedColor,
            ...(!$isOrientationHorizontal && {
                backgroundColor: $activeBackgroundColor
            })
        },
        ...(!$isOrientationHorizontal && {
            '&[aria-selected="true"]::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '4px',
                backgroundColor: $selectedColor,
                borderRadius: '0 2px 2px 0'
            }
        })
    })
);

const TabsImpl = React.forwardRef<HTMLDivElement, TabsProps>((props, ref) => {
    const {
        contentHeader,
        selectedTabIndex,
        onTabChange,
        sticky,
        labelTextAlign
    } = props;

    const [internalSelectedIndex, setInternalSelectedIndex] = React.useState(
        selectedTabIndex ?? 0
    );

    const isControlled = selectedTabIndex !== undefined;
    const activeTabIndex = isControlled
        ? selectedTabIndex
        : internalSelectedIndex;

    React.useEffect(() => {
        if (isControlled && selectedTabIndex !== internalSelectedIndex) {
            setInternalSelectedIndex(selectedTabIndex);
        }
    }, [selectedTabIndex, isControlled, internalSelectedIndex]);

    const orientation = props.orientation ?? 'horizontal';
    const isOrientationHorizontal = orientation === 'horizontal';
    const activeTabHeader = React.useMemo(
        () => props.items[activeTabIndex]?.header,
        [activeTabIndex, props.items]
    );

    const isSticky = Boolean(sticky);
    const stickyConfig = typeof sticky === 'object' ? sticky : {};
    const stickyTop = stickyConfig.top ?? 0;
    const stickyBackgroundColor =
        usePaletteColor(stickyConfig.backgroundColor) ?? 'initial';
    const { contentPadding } = stickyConfig;
    const indicatorColor =
        usePaletteColor('components.tab.primary.font-active') ?? undefined;

    return (
        <FlexBox
            ref={ref}
            data-role="tabs"
            gap={props.contentGap ?? 2}
            stack={isOrientationHorizontal}
            height="100%"
            minHeight={0}
            overflow={isSticky ? 'visible' : 'auto'}
            stretch={isOrientationHorizontal}
        >
            <StyledFlexBox
                gap={isOrientationHorizontal ? 0 : 0.75}
                stack={isOrientationHorizontal}
                $isSticky={isSticky}
                $stickyTop={stickyTop}
                $stickyBackgroundColor={stickyBackgroundColor}
                $contentPadding={contentPadding}
                $isOrientationHorizontal={isOrientationHorizontal}
            >
                <StyledMuiTabs
                    orientation={orientation}
                    value={activeTabIndex}
                    $isOrientationHorizontal={isOrientationHorizontal}
                    $indicatorColor={
                        isOrientationHorizontal ? indicatorColor : undefined
                    }
                    slotProps={{
                        indicator: { hidden: !isOrientationHorizontal }
                    }}
                    onChange={(_e, newTabIndex: number) => {
                        if (!isControlled) {
                            setInternalSelectedIndex(newTabIndex);
                        }

                        onTabChange?.(newTabIndex);
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {props.items.map((item, index) => {
                        const LabelComponent = () => {
                            return (
                                <TabLabel
                                    isOrientationHorizontal={
                                        isOrientationHorizontal
                                    }
                                    labelTextAlign={labelTextAlign}
                                    item={item}
                                    selected={index === activeTabIndex}
                                />
                            );
                        };
                        return (
                            <Tab
                                key={item['data-role'] ?? index}
                                data-role={item['data-role']}
                                disabled={item.disabled}
                                label={<LabelComponent />}
                                labelTextAlign={
                                    !isOrientationHorizontal
                                        ? (labelTextAlign as string)
                                        : undefined
                                }
                                isOrientationHorizontal={
                                    isOrientationHorizontal
                                }
                            />
                        );
                    })}
                </StyledMuiTabs>
                {isOrientationHorizontal && (
                    <Divider orientation="horizontal" />
                )}
            </StyledFlexBox>
            <>
                {contentHeader}
                {activeTabHeader && (
                    <FlexBox width="100%">
                        <FlexBox stretch vAlign="center">
                            <Typography variant="headingMedium">
                                {activeTabHeader.title}
                            </Typography>
                            {Boolean(activeTabHeader.info) && (
                                <IconButton
                                    size="small"
                                    tooltip={activeTabHeader.info}
                                    icon="IconInfo"
                                />
                            )}
                        </FlexBox>
                        <FlexBox gap={1}>
                            {activeTabHeader.actions?.map((action, index) => {
                                return (
                                    <Button
                                        key={index}
                                        variant="contained"
                                        onClick={action.onClick}
                                    >
                                        {action.title}
                                    </Button>
                                );
                            })}
                        </FlexBox>
                    </FlexBox>
                )}
                {props.items.map((item, index) => {
                    const isActive = index === activeTabIndex;
                    if (!isActive && !item.keepMounted) {
                        return null;
                    }

                    const ContentComponent = item.content;
                    const hideTabContent = !isActive && item.keepMounted;

                    return (
                        <FlexBox
                            key={item['data-role'] ?? index}
                            data-role="tab-content"
                            minHeight={0}
                            {...(hideTabContent && {
                                height: '100%',
                                position: 'absolute',
                                visibility: 'hidden',
                                pointerEvents: 'none'
                            })}
                            stretch
                        >
                            <FlexBox width="100%">
                                <ContentComponent />
                            </FlexBox>
                        </FlexBox>
                    );
                })}
            </>
        </FlexBox>
    );
});

(TabsImpl as React.NamedExoticComponent).displayName = 'Tabs';
export const Tabs = withVersion(TabsImpl, '1.2.0');

const Tab = React.forwardRef<
    HTMLDivElement,
    MuiTabProps & { labelTextAlign?: string; isOrientationHorizontal?: boolean }
>(({ labelTextAlign, isOrientationHorizontal, ...props }, ref) => {
    const color = usePaletteColor('components.tab.primary.font');
    const selectedColor = usePaletteColor('components.tab.primary.font-active');
    const backgroundActiveColor = usePaletteColor(
        'components.tab.primary.bg-active-vertical'
    );

    return (
        <StyledMuiTab
            ref={ref}
            {...props}
            $color={color}
            $selectedColor={selectedColor}
            $activeBackgroundColor={backgroundActiveColor}
            $labelTextAlign={labelTextAlign}
            $isOrientationHorizontal={isOrientationHorizontal}
        />
    );
});
